import React from 'react';
import Hls from 'hls.js';

/**
 * @file format-time.js
 *
 * Format seconds as a time string, H:MM:SS or M:SS
 * Supplying a guide (in seconds) will force a number of leading zeros
 * to cover the length of the guide
 *
 * @param  {Number} seconds Number of seconds to be turned into a string
 * @param  {Number} guide   Number (in seconds) to model the string after
 * @return {String}         Time formatted as H:MM:SS or M:SS
 * @private
 * @function formatTime
 */
export function formatTime(_seconds = 0, guide = _seconds, dvr = false) {
  let seconds = dvr ? (guide - _seconds) : _seconds;
  let s = Math.floor(seconds % 60);
  let m = Math.floor((seconds / 60) % 60);
  let h = Math.floor(seconds / 3600);
  const gm = Math.floor((guide / 60) % 60);
  const gh = Math.floor(guide / 3600);

  // handle invalid times
  if (isNaN(seconds) || seconds === Infinity) {
    // '-' is false for all relational operators (e.g. <, >=) so this setting
    // will add the minimum number of fields specified by the guide
    h = m = s = '-';
  }

  // Check if we need to show hours
  h = (h > 0 || gh > 0) ? `${h}:` : '';

  // If hours are showing, we may need to add a leading zero.
  // Always show at least one digit of minutes.
  m = `${(((h || gm >= 10) && m < 10) ? `0${m}` : m)}:`;

  // Check if leading zero is need for seconds
  s = (s < 10) ? `0${s}` : s;

  if (dvr) return '-' + h + m + s;
  return h + m + s;
}

// Check if the element belongs to a video element
// only accept <source />, <track />,
// <MyComponent isVideoChild />
// elements
export function isVideoChild(c) {
  if (c.props && c.props.isVideoChild) {
    return true;
  }
  return (c.type === 'source' || c.type === 'track');
}

const find = (elements, func) => elements.filter(func)[0];

// check if two components are the same type
const isTypeEqual = (component1, component2) => {
  const type1 = component1.type;
  const type2 = component2.type;

  if (typeof type1 === 'string' || typeof type2 === 'string') {
    return type1 === type2;
  }

  if (typeof type1 === 'function' && typeof type2 === 'function') {
    return type1.displayName === type2.displayName;
  }

  return false;
};

// is hls source?
export function isHls(src) {
  const hlsSuffix = /http.*?\.m3u8/;
  if (hlsSuffix.test(src)) return true;

  return false;
}

// merge default children
// sort them by `order` property
// filter them by `disabled` property
export function mergeAndSortChildren(defaultChildren, _children, _parentProps, defaultOrder = 1) {
  const children = React.Children.toArray(_children);
  const parentProps = { ..._parentProps };
  return children
    .filter((e) => !e.props.disabled) // filter the disabled components
    .concat(
      defaultChildren.filter(
        (c) => !find(children, (component) => isTypeEqual(component, c))
      )
    )
    .map((element) => {
      const defaultComponent = find(defaultChildren, (c) => isTypeEqual(c, element));
      delete parentProps.order;
      const defaultProps = defaultComponent ? defaultComponent.props : {};
      const props = {
        ...parentProps, // inherit from parent component
        ...defaultProps, // inherit from default component
        ...element.props, // element's own props
      };
      const e = React.cloneElement(
        element,
        props,
        element.props.children
      );
      return e;
    })
    .sort((a, b) => (a.props.order || defaultOrder) - (b.props.order || defaultOrder));
}

/**
 * Temporary utility for generating the warnings
 */
export function deprecatedWarning(oldMethodCall, newMethodCall) {
  // eslint-disable-next-line no-console
  console.warn(`WARNING: ${oldMethodCall} will be deprecated soon! Please use ${newMethodCall} instead.`);
}

export function throttle(callback, limit) {
  let wait = false;
  return () => {
    if (!wait) {
      callback.apply(null, arguments);
      wait = true;
      setTimeout(() => {
        wait = false;
      }, limit);
    }
  };
}

export const mediaProperties = [
  'error', 'src', 'srcObject',
  'currentSrc', 'crossOrigin',
  'networkState', 'preload', 'buffered',
  'readyState', 'seeking', 'currentTime',
  'duration', 'paused', 'defaultPlaybackRate',
  'playbackRate', 'played', 'seekable',
  'ended', 'autoplay', 'loop',
  'mediaGroup', 'controller', 'controls',
  'volume', 'muted', 'defaultMuted',
  'audioTracks', 'videoTracks', 'textTracks',
  'width', 'height', 'videoWidth', 'videoHeight',
  'poster'
];

export function getErrorSchema(error) {
  if (error) {
    const { details, fatal } = error,
      isFatal = fatal || isNetworkError(error);
    if (isFatal) {
      return {
        type: getErrorType(error),
        detail: isNetworkError(error) ? getNetworkError(error) : getErrorDetail(details),
        isFatal
      };
    }
    return null;
  }
}

function getNetworkError(error) {
  if (error) {
    const { response, details } = error,
      detail = getErrorDetail(details);
    if (response && response.code) {
      return `HTTP_ERROR_${response.code}`;
    }
    return detail ? `NETWORK_ISSUE_${getErrorDetail(details)}` : 'NETWORK_ERROR';
  }
}

function isNetworkError(error) {
  return error && error.type === Hls.ErrorTypes.NETWORK_ERROR;
}

function getErrorType(error) {
  const { type } = error;
  switch (type) {
    case  Hls.ErrorTypes.KEY_SYSTEM_ERROR:
      return 'KEY_SYSTEM_ERROR';
    case  Hls.ErrorTypes.MEDIA_ERROR:
      return 'MEDIA_ERROR';
    case  Hls.ErrorTypes.MUX_ERROR:
      return 'MUX_ERROR';
    case  Hls.ErrorTypes.NETWORK_ERROR:
      return 'NETWORK_ERROR';
    case  Hls.ErrorTypes.OTHER_ERROR:
      return 'OTHER_ERROR';
    default:
      return 'UNHANDLED_ERROR';
  }
}

function getErrorDetail(errorDetail) {
  if (errorDetail) {
    switch (errorDetail) {
      case Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR:
        return 'AUDIO_TRACK_LOAD_ERROR';
      case Hls.ErrorDetails.AUDIO_TRACK_LOAD_TIMEOUT:
        return 'AUDIO_TRACK_LOAD_TIMEOUT';
      case Hls.ErrorDetails.BUFFER_ADD_CODEC_ERROR:
        return 'BUFFER_ADD_CODEC_ERROR';
      case Hls.ErrorDetails.BUFFER_APPENDING_ERROR:
        return 'BUFFER_APPENDING_ERROR';
      case Hls.ErrorDetails.BUFFER_APPEND_ERROR:
        return 'BUFFER_APPEND_ERROR';
      case Hls.ErrorDetails.BUFFER_FULL_ERROR:
        return 'BUFFER_FULL_ERROR';
      case Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL:
        return 'BUFFER_NUDGE_ON_STALL';
      case Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE:
        return 'BUFFER_SEEK_OVER_HOLE';
      case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
        return 'BUFFER_STALLED_ERROR';
      case Hls.ErrorDetails.FRAG_DECRYPT_ERROR:
        return 'FRAG_DECRYPT_ERROR';
      case Hls.ErrorDetails.FRAG_LOAD_ERROR:
        return 'FRAG_LOAD_ERROR';
      case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
        return 'FRAG_LOAD_TIMEOUT';
      case Hls.ErrorDetails.FRAG_PARSING_ERROR:
        return 'FRAG_PARSING_ERROR';
      case Hls.ErrorDetails.INTERNAL_EXCEPTION:
        return 'INTERNAL_EXCEPTION';
      case Hls.ErrorDetails.KEY_LOAD_ERROR:
        return 'KEY_LOAD_ERROR';
      case Hls.ErrorDetails.KEY_LOAD_TIMEOUT:
        return 'KEY_LOAD_TIMEOUT';
      case Hls.ErrorDetails.KEY_SYSTEM_LICENSE_REQUEST_FAILED:
        return 'KEY_SYSTEM_LICENSE_REQUEST_FAILED';
      case Hls.ErrorDetails.KEY_SYSTEM_NO_ACCESS:
        return 'KEY_SYSTEM_NO_ACCESS';
      case Hls.ErrorDetails.KEY_SYSTEM_NO_KEYS:
        return 'KEY_SYSTEM_NO_KEYS';
      case Hls.ErrorDetails.KEY_SYSTEM_NO_SESSION:
        return 'KEY_SYSTEM_NO_SESSION';
      case Hls.ErrorDetails.LEVEL_LOAD_ERROR:
        return 'LEVEL_LOAD_ERROR';
      case Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT:
        return 'LEVEL_LOAD_TIMEOUT';
      case Hls.ErrorDetails.LEVEL_SWITCH_ERROR:
        return 'LEVEL_SWITCH_ERROR';
      case Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR:
        return 'MANIFEST_INCOMPATIBLE_CODECS_ERROR';
      case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
        return 'MANIFEST_LOAD_ERROR';
      case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
        return 'MANIFEST_LOAD_TIMEOUT';
      case Hls.ErrorDetails.MANIFEST_PARSING_ERROR:
        return 'MANIFEST_PARSING_ERROR';
      case Hls.ErrorDetails.REMUX_ALLOC_ERROR:
        return 'REMUX_ALLOC_ERROR';
      default :
        return typeof errorDetail === 'string' ? camelCaseSeparator(errorDetail)
          .toUpperCase() : 'OTHER_ERRORS';
    }
  }
  return 'UNHANDLED_ERROR';
}

// function isHandalableError(fatal) {
//   return fatal !== true;
// }

export function camelCaseSeparator(input, separator = '_') {
  if (input && separator) {
    return input.trim()
      .replace(/([A-Z])/g, (item) => {
        return separator + item.toLowerCase();
      })
      .replace(new RegExp(`^\\${separator}`, 'g'), '')
      .replace(new RegExp(`\\${separator}$`, 'g'), '');
  }
  return null;
}

export function startTimer() {
  let milliSecondsPassed = 0;
  const timerInterval = setInterval(() => {
    milliSecondsPassed += 100;
  }, 100);
  return {
    end: () => {
      clearInterval(timerInterval);
      return milliSecondsPassed;
    }
  };
}
